import type { RequestInit } from '@vercel/fetch'
import { isExpiredCustomerSessionError } from './authErrors'
import { getDeviceHeaderValue } from '../../lib/utils/pwa'
import { logFetchQueryAsCurl, logGqlErrors } from './logGraphqlFetchErrors'

const defaultLocale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'en'

export interface GraphQLError {
    message: string
    extensions: {
        category: string
    }
    locations: Array<{
        line: number
        column: number
    }>
    debugMessage: string
}

// Magento 2.4.8 valida el header Authorization en TODA request GraphQL, antes
// de parsear la query (CustomerGraphQl AuthorizationRequestValidator). Un token
// vencido devuelve 401 incluso en queries publicas -- menu, banners, storeConfig,
// ruteo -- y la respuesta llega sin clave `data`, asi que la pagina se rompe.
// El token es un JWS HS256, con lo que el `exp` del payload se puede leer en el
// cliente sin la clave de firma: si ya vencio, no lo enviamos.
const TOKEN_STORAGE_KEY = 'pylot_token'
const CART_STORAGE_KEY = 'cart_id'
const TOKEN_SKEW_SECONDS = 30

// Guardamos el valor del token rechazado, no un booleano: asi un login nuevo en
// la misma sesion de pagina vuelve a adjuntar su token sin resetear nada.
let rejectedToken: string | null = null

/**
 * Marca un token como inservible para el resto de la sesion de pagina.
 *
 * `clearStorage` distingue los dos casos de uso:
 * - true  -> lo detectamos nosotros (vencido). Nadie mas se va a enterar, y
 *            queremos que `getIsSignedIn` refleje que ya no hay sesion.
 * - false -> lo rechazo el backend. Dejamos el borrado y el aviso al usuario a
 *            cargo de use-query, que ya muestra el toast de sesion expirada.
 */
const rejectToken = (token: string, clearStorage: boolean): void => {
    rejectedToken = token
    if (clearStorage && typeof window !== 'undefined') {
        window.localStorage.removeItem(TOKEN_STORAGE_KEY)
        window.localStorage.removeItem(CART_STORAGE_KEY)
    }
}

// Solo para tests: limpia el estado de modulo entre casos.
export const resetRejectedToken = (): void => {
    rejectedToken = null
}

const getJwtExpiry = (token: string): number | null => {
    const payload = token.split('.')[1]
    if (!payload) return null
    try {
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
        const { exp } = JSON.parse(atob(padded))
        return typeof exp === 'number' ? exp : null
    } catch {
        return null
    }
}

/** True solo si el proximo request va a llevar Authorization. */
export const hasUsableCustomerToken = (): boolean => {
    if (typeof window === 'undefined') {
        return false
    }
    return getUsableToken() !== null
}

const getUsableToken = (): string | null => {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY)
    if (!token || token === rejectedToken) return null
    const expiry = getJwtExpiry(token)
    // expiry null => no es un JWS legible. Lo mandamos y que decida el backend:
    // es preferible un 401 aislado a cortar una sesion valida por un cambio de
    // formato de token.
    if (expiry !== null && expiry - TOKEN_SKEW_SECONDS <= Date.now() / 1000) {
        rejectToken(token, true)
        return null
    }
    return token
}

const hasAuthError = (resJson: { errors?: GraphQLError[] }): boolean =>
    Array.isArray(resJson?.errors) &&
    resJson.errors.some((error) => isExpiredCustomerSessionError(error))

// This will remove all space from queries except absolute minimum spaces
const getShortQueryString = (query: string): string => {
    return query
        .replace(/#.*\n/g, '')
        .replace(/[\s|,]*\n+[\s|,]*/g, ' ')
        .replace(/:\s/g, ':')
        .replace(/,\s/g, ',')
        .replace(/\)\s\{/g, '){')
        .replace(/\}\s/g, '}')
        .replace(/\{\s/g, '{')
        .replace(/\s\}/g, '}')
        .replace(/\s\{/g, '{')
        .replace(/\)\s/g, ')')
        .replace(/\(\s/g, '(')
        .replace(/\s\)/g, ')')
        .replace(/\s\(/g, '(')
        .replace(/=\s/g, '=')
        .replace(/\s=/g, '=')
        .replace(/@\s/g, '@')
        .replace(/\s@/g, '@')
        .replace(/\s\$/g, '$')
        .replace(/\s\./g, '.')
        .trim()
}

enum QueryType {
    query = 'query',
    mutation = 'mutation',
    subscription = 'subscription'
}

type GetQueryParamsResult = {
    operationType: QueryType
    operationNickname: string
    firstQueryName: string
}

/**
 * Extracts query parameters from shortened graphql query
 * @param query short query string (processed with `getShortQueryString`)
 * @returns query parameters necessary for custom headers and request body
 */
const getQueryParams = (query: string): GetQueryParamsResult => {
    const operationMethods = Object.keys(operationToMethod).join('|')
    const queryParamExtractionRegExp = new RegExp(
        `^(?:(${operationMethods}) ?)?(?:(\\w+)(?:\\(.+?\\))?)?\\{(?:\\w+:)?(\\w+)`
    )

    const params = queryParamExtractionRegExp.exec(query) || []

    if (!params) {
        const errorMessage = `Unable to get query parameters, i.e. 'type', 'operationNickname', 'firstQueryName', from the following query: "${query}"`

        console.error(errorMessage)
        throw new Error(errorMessage)
    }

    const firstQueryName = params[3]
    const [, operationType = QueryType.query, operationNickname] = params

    return {
        operationType: operationType as QueryType,
        operationNickname,
        firstQueryName
    }
}

const operationToMethod = {
    [QueryType.query]: 'GET',
    [QueryType.mutation]: 'POST',
    [QueryType.subscription]: 'POST'
}

export type GraphqlFetchOutput<OutputInterface> = {
    data: OutputInterface
    errors: GraphQLError[]
}

export type GraphqlFetchOutputPromise<OutputInterface> = Promise<
    GraphqlFetchOutput<OutputInterface>
>

export type GraphqlFetchParameters<VariablesInterface> = {
    query: string
    variables?: VariablesInterface
    locale?: string
    fetchOptions?: RequestInit
    customURL?: string
    device?: string
    customPylotQueryHeader?: string
}

export const graphqlFetch = async <VariablesInterface, OutputInterface>({
    query,
    variables,
    locale,
    fetchOptions,
    customPylotQueryHeader,
    customURL,
    device
}: GraphqlFetchParameters<VariablesInterface>): GraphqlFetchOutputPromise<OutputInterface> => {
    // Los reintentos solo aplican a lecturas. Reintentar una mutacion puede crear
    // la orden dos veces (placeOrder) o abrir dos transacciones en la pasarela
    // (createWebpayTransaction y sus pares): el primer intento alcanza a llegar al
    // backend y el reintento choca contra la quote ya desactivada.
    const { operationType } = getQueryParams(getShortQueryString(query))
    const MAX_RETRIES = operationType === QueryType.query ? 3 : 1
    let lastError: Error | null = null

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            return await graphqlFetchInternal<
                VariablesInterface,
                OutputInterface
            >({
                query,
                variables,
                locale,
                fetchOptions,
                customPylotQueryHeader,
                customURL,
                device
            })
        } catch (e: any) {
            lastError = e
            if (
                e.message.includes('Transient backend error') ||
                e.message.includes('Failed to connect to the service')
            ) {
                console.warn(`Attempt ${attempt + 1} failed. Retrying...`)
                await new Promise((resolve) =>
                    setTimeout(resolve, 1000 * (attempt + 1))
                )
                continue
            }
            throw e
        }
    }
    throw lastError || new Error('Unknown error in graphqlFetch')
}

const graphqlFetchInternal = async <VariablesInterface, OutputInterface>({
    query,
    variables,
    locale,
    fetchOptions,
    customPylotQueryHeader,
    customURL,
    device
}: GraphqlFetchParameters<VariablesInterface>): GraphqlFetchOutputPromise<OutputInterface> => {
    const resolvedFetchOptions = fetchOptions ?? {}
    const shortQuery = getShortQueryString(query)
    const { operationType, operationNickname, firstQueryName } =
        getQueryParams(shortQuery)

    const operationNicknameFormatted = operationNickname
        ? `&operationName=${operationNickname}`
        : ''

    const httpMethod =
        resolvedFetchOptions.method || operationToMethod[operationType]
    const isPostMethod = httpMethod === 'POST'

    // get the URL ending for queries with parameters
    // leave it empty for mutations or when using POST
    const urlEnding =
        operationType === QueryType.query && !isPostMethod
            ? `?query=${encodeURIComponent(
                  shortQuery
              )}${operationNicknameFormatted}&variables=${encodeURIComponent(
                  JSON.stringify(variables || {})
              )}`
            : ''

    const headerExtras =
        resolvedFetchOptions.headers != null &&
        typeof resolvedFetchOptions.headers === 'object'
            ? resolvedFetchOptions.headers
            : {}

    const headers = {
        ...headerExtras,
        'x-pylot-query': customPylotQueryHeader
            ? customPylotQueryHeader
            : firstQueryName
    } as Record<string, any>

    if (isPostMethod) {
        headers['Content-Type'] = 'application/json'
    }

    const resolvedDevice = device || getDeviceHeaderValue()

    if (resolvedDevice) {
        headers['Device'] = resolvedDevice
    }

    if (
        !headers['x-pylot-backend'] &&
        process.env.NEXT_PUBLIC_X_PYLOT_BACKEND
    ) {
        headers['x-pylot-backend'] = process.env.NEXT_PUBLIC_X_PYLOT_BACKEND
    }

    if (
        process.env.NEXT_PUBLIC_STORE_CODE &&
        locale &&
        locale !== defaultLocale
    ) {
        headers.Store = `${process.env.NEXT_PUBLIC_STORE_CODE}_${locale}`
    } else if (process.env.NEXT_PUBLIC_STORE_CODE) {
        headers.Store = process.env.NEXT_PUBLIC_STORE_CODE
    } else if (locale && locale !== defaultLocale) {
        headers.Store = locale
    }

    let sentToken: string | null = null

    if (typeof window !== 'undefined') {
        sentToken = getUsableToken()
        if (sentToken) {
            headers.Authorization = `Bearer ${sentToken}`
        }
    }

    // init the fetch config early so we can manipulate below
    const fetchConfig = {
        ...resolvedFetchOptions,
        method: httpMethod,
        headers
    }

    // Add body if using POST method (for mutations or queries forced to POST)
    if (isPostMethod) {
        fetchConfig.body = JSON.stringify({
            query,
            variables
        })
    }

    const fetchUrl =
        (customURL || process.env.NEXT_PUBLIC_API_GATEWAY_URL) + urlEnding
    let res
    try {
        res = await fetch(
            fetchUrl,
            // @ts-ignore for some reason, typescript insists that we need `body`,
            // which is only appropriate for POST requests
            fetchConfig
        )
    } catch (e: any) {
        // Log service connection errors
        logFetchQueryAsCurl(fetchUrl, fetchConfig)
        throw new Error(`Failed to connect to the service: ${e.message}`)
    }

    // We don't return 'await res.json()' right away in order to be able to log erroneous response text and headers
    const resText = await res.text()
    try {
        if (
            resText.includes('upstream connect error') ||
            resText.includes('disconnect/reset before headers')
        ) {
            // If it's a known transient error from the proxy, we can try to throw a more specific error
            // or even implement a retry here. For now, let's just make it clear.
            throw new Error(`Transient backend error: ${resText}`)
        }
        const resJson = JSON.parse(resText)
        if (resJson && (resJson['errors'] || resJson['error'])) {
            logGqlErrors(fetchUrl, fetchConfig, res, resJson)
        }
        // Mandamos un token y el backend lo rechazo: no lo volvemos a adjuntar
        // en el resto de la sesion de pagina. En una carga tipica salen 8-15
        // queries en paralelo y todas fallarian con el mismo 401.
        if (sentToken && hasAuthError(resJson)) {
            rejectToken(sentToken, false)
        }
        return resJson
    } catch (e: any) {
        // Show the actual response text and error message
        if (e.message.includes('Transient backend error')) {
            throw e
        }
        console.error(e)
        logFetchQueryAsCurl(fetchUrl, fetchConfig)
        throw new Error(`Failed to process response as JSON data: ${resText}`)
    }
}
